import { useContext, useRef, useState } from "react";
import { useStyles } from "./RCAAddObservationModal.styles";
import { Box, InputLabel, TextField, Typography } from "@mui/material";
import { Form, Formik, FormikProps } from "formik";
import { useTranslation } from "react-i18next";
import { addObservationFormValidationSchema } from "validations/schemas";
import MainModal from "components/MainModal";
import { useRCAMainStyles } from "pages/Corporate/RCA/RCA.styles";
import DoneIcon from "@mui/icons-material/Done";
import { CorporateContext } from "contexts/CorporateContext/CorporateContext";
import { IssueEntity } from "entities/RCA/IssueEntity/IssueEntity";
import { IRCAAddObservationModal, IRCAAddObservationModalFormValues } from "./RCAAddObservationModalInterfaces";
import { RCALibraryEntity } from "entities/RCA/RCALibraryEntity/RCALibraryEntity";

const RCAAddObservationModal = ({ open, onClose, onAddObservation, id, rca }: IRCAAddObservationModal) => {
    const classes = useStyles();
    const mainClasses = useRCAMainStyles();
    const [isSaving, setIsSaving] = useState(false);

    const handleOnClose = () => {
        onClose();
    };
    const { t } = useTranslation();
    const formRef = useRef<FormikProps<IRCAAddObservationModalFormValues>>(null);
    const { currentUser, isLoadingCurrentUser } = useContext(CorporateContext);
    const { createObservationIssue } = IssueEntity.InsertObservationIssue();
    const { createObservationRCA } = RCALibraryEntity.InsertObservationRCA();

    const initFormValues: IRCAAddObservationModalFormValues = {
        description: "",
    };

    const handleSubmit = async (values: IRCAAddObservationModalFormValues) => {
        setIsSaving(true);
        const observationValues = {
            description: values.description,
            date: new Date().toISOString(),
            ...(rca ? { rca_id: id } : { issue_id: id }),
            user_id: currentUser?.user_id,
        };

        try {
            const response = rca
                ? await createObservationRCA({ variables: { observationRCA: observationValues } })
                : await createObservationIssue({ variables: { observationIssue: observationValues } });
            onAddObservation();
            setIsSaving(false);
            if (response) {
                onClose();
            }
        } catch {
            setIsSaving(false);
            return;
        }
    };

    return (
        <MainModal
            isOpen={open}
            onClose={handleOnClose}
            title={t("rca.rca_library.rca_detail.observation_modal.title")}
            onOkButtonClick={() => {
                if (formRef.current) {
                    formRef.current.handleSubmit();
                }
            }}
            disableOkButton={isSaving}
            disableEscapeKeyDown
            disableCancelButton={isSaving}
            okButton={
                <>
                    <DoneIcon />
                    {t("rca.rca_library.rca_detail.observation_modal.create")}
                </>
            }
            cancelButton={t("rca.rca_library.rca_detail.observation_modal.cancel")}
            contentClassName={classes.content}
        >
            <div className={classes.form}>
                <Formik
                    innerRef={formRef}
                    initialValues={initFormValues}
                    validationSchema={addObservationFormValidationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ isSubmitting, errors, touched, values, handleChange }) => {
                        return (
                            <Form className={mainClasses.contentForm}>
                                <Box className={mainClasses.contentFormBox}>
                                    <InputLabel id="outlined-description-label">
                                        <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                                            {t("rca.rca_library.rca_detail.observation_modal.description")}
                                        </Typography>
                                    </InputLabel>
                                    <TextField
                                        disabled={isSaving || isSubmitting || isLoadingCurrentUser}
                                        aria-labelledby="outlined-description-label"
                                        fullWidth
                                        multiline
                                        rows={3}
                                        id="outlined-description-input"
                                        className={mainClasses.contentFormMultitextField}
                                        name="description"
                                        type="text"
                                        variant="outlined"
                                        value={values.description}
                                        onChange={handleChange}
                                        error={touched.description && Boolean(errors.description)}
                                        helperText={touched.description && errors.description && t(errors.description)}
                                    />
                                </Box>
                            </Form>
                        );
                    }}
                </Formik>
            </div>
        </MainModal>
    );
};

export default RCAAddObservationModal;
